import { createReducer, on, Action } from '@ngrx/store'
import { MeasurementUnits } from 'src/app/common/measurement-units.model'
import { NetworkNodeType } from '../../flight-network/shared/network_node.model'
import * as fromNetwork from '../actions/flight-network.actions'

export enum CenterStatus {
  Open = 'open',
  Closing = 'closing',
  CanBeClosed = 'can_be_closed',
  Closed = 'closed',
}

export interface ChecklistInstructionState {
  caption: string
}

export interface ChecklistState {
  id: number
  name: string
  owner: string
  checklist_type: string
  instructions: ChecklistInstructionState[]
}

export interface VehicleState {
  id: number
  name: string
  registration_number?: string
  center_id: number
  center_name?: string
  checklists: { checklist: ChecklistState }[]
  is_in_service: boolean
  is_parked: boolean
}

export interface FlightNetworkCenterState {
  id: number
  checklists: { checklist: ChecklistState }[]
  name: string
  node_id: number
  network_id: number
  open_state: CenterStatus
  vehicles: VehicleState[]
  timezone: string
  email_addresses: FlightNetworkCenterEmailAddress[]
  measurement_units?: MeasurementUnits
}

export interface FlightNetworkCenterEmailAddress {
  id?: number
  email_address: string
}

export interface FlightNetworkNodeState {
  id: number
  name: string
  flight_altitude_m: number
  ground_elevation_m: number
  latitude_deg: number
  longitude_deg: number
  network_id: number
  node_type: NetworkNodeType
  uuid: string
  is_test_point: boolean
}

export interface FlightNetworkEdgeState {
  id: number
  from_vertex_id: number
  to_vertex_id: number
  network_id: number
}

export interface FlightNetworkOperatorState {
  id: number
  name: string
}

export interface FlightNetworkState {
  id: number
  name: string
  operator_id: number
  centers: FlightNetworkCenterState[]
  edges: FlightNetworkEdgeState[]
  nodes: FlightNetworkNodeState[]
  operator: FlightNetworkOperatorState
  version: number
  safe_up_land_timeout_sec: number
}

export interface State {
  network: FlightNetworkState
  loading: boolean
  error: any
}

export const initialState: State = {
  network: null,
  loading: false,
  error: null,
}

const userReducer = createReducer(
  initialState,
  on(fromNetwork.loadFlightNetwork, fromNetwork.editFlightNetwork, (state) => ({ ...state, loading: true })),

  on(fromNetwork.loadFlightNetworkSuccess, (state, { network }) => {
    const newCenters = network.centers
    return {
      ...state,
      network: {
        ...network,
        centers: newCenters,
      },
      loading: false,
    }
  }),

  on(fromNetwork.editFlightNetworkSuccess, (state) => ({ ...state, loading: false })),

  on(fromNetwork.loadFlightNetworkFailure, fromNetwork.editFlightNetworkFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(fromNetwork.updateFlightNetworkCenter, (state, { center }) => {
    const centers = state.network?.centers.map((item) => (item.id === center.id ? { ...center } : { ...item }))
    return {
      ...state,
      network: {
        ...state.network,
        centers: centers || [],
      },
    }
  }),

  on(fromNetwork.updateFlightNetworkCenterOpenState, (state, { center_id, open_state }) => {
    let centers = []
    if (state.network) {
      centers = state.network.centers
    }
    const maybeFoundCenter = centers.find(({ id }) => id === center_id)
    if (maybeFoundCenter) {
      maybeFoundCenter.open_state = open_state
    }

    return {
      ...state,
      network: {
        ...state.network,
        centers,
      },
    }
  }),
)

export function reducer(state: State | undefined, action: Action) {
  return userReducer(state, action)
}

export const selectors = {
  getNetwork: (state: State) => state?.network,
  getNetworkLoading: (state: State) => state?.loading,
  getNetworkCenter: (state: State) => {
    return state.network?.nodes.find((item) => item.node_type === NetworkNodeType.Center)
  },
  getNetworkError: (state: State) => state.error,
  getNetworkCenterLatLng: (state: State) => {
    if (!state.network) {
      return null
    }
    const center = state.network.nodes.find((item) => item.node_type === NetworkNodeType.Center)
    return new google.maps.LatLng(center.latitude_deg, center.longitude_deg)
  },
  getNetworkCenters: (state: State) => state?.network?.centers ?? [],
  getNetworkCenterById: (state: State, props: { id: number }) => {
    if (!state.network) {
      return null
    }
    return state.network.centers.find(({ id }) => id === props.id)
  },
}
